import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Send, RotateCcw, Sparkles, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ContactDialog from "@/components/ContactDialog";
import AuditDialog from "@/components/AuditDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "anthony_chat_session_id";

const CHIPS = [
  { label: "Audit my website", prompt: "__OPEN_AUDIT__", kind: "audit" },
  { label: "What do you actually do?", prompt: "What do you actually do?" },
  { label: "Inclusive AI", prompt: "What does inclusive AI mean to you?" },
  { label: "Book Anthony", prompt: "How can I book you to speak or consult?" },
  {
    label: "Neurodivergent + AI",
    prompt:
      "How does being neurodivergent shape the way you think about AI?",
  },
  { label: "AI bias", prompt: "What's the most overlooked form of AI bias?" },
];

function slug(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Render assistant text with clickable URLs (no markdown — just bare URLs).
function renderRichText(text) {
  if (!text) return null;
  const urlRe = /(https?:\/\/[^\s<>()[\]]+[^\s<>()[\].,;:!?'"])/g;
  const parts = text.split(urlRe);
  return parts.map((part, i) => {
    if (urlRe.test(part)) {
      // re-create the regex because split's interleaving doesn't reset state
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noreferrer"
          className="text-[#F4ECE0] underline underline-offset-4 decoration-[rgba(244,236,224,0.45)] hover:decoration-[rgba(244,236,224,0.85)] transition-colors duration-150"
        >
          {part.replace(/^https?:\/\//, "")}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// Detect when the assistant is steering the user to the contact form,
// so we can offer a one-tap button right under the message.
function suggestsContactForm(text) {
  if (!text) return false;
  const t = text.toLowerCase();
  return (
    t.includes('"email me" button') ||
    t.includes("email me button") ||
    t.includes("use the email me") ||
    t.includes("tap email me") ||
    t.includes("hit the email me") ||
    t.includes("contact form") ||
    t.includes("drop me an email")
  );
}

// Detect when the assistant is steering the user to the audit tool.
function suggestsAudit(text) {
  if (!text) return false;
  const t = text.toLowerCase();
  return (
    t.includes('"audit my website"') ||
    t.includes("audit my website") ||
    t.includes("audit wizard") ||
    t.includes("audit tool") ||
    t.includes("run an audit") ||
    t.includes("run the audit") ||
    t.includes("open the audit")
  );
}

// Detect when the user types something that should trigger the audit dialog directly.
function userWantsAudit(text) {
  if (!text) return false;
  const t = text.toLowerCase().trim();
  return (
    t === "audit my website" ||
    t === "audit my site" ||
    t === "audit my charity" ||
    t === "audit my school" ||
    t.startsWith("audit my ")
  );
}

export default function ChatPanel() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hydrating, setHydrating] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactReason, setContactReason] = useState(undefined);
  const [auditOpen, setAuditOpen] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Initialise session id
  useEffect(() => {
    let id = null;
    try {
      id = localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      // ignore
    }
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch (_e) {
        // ignore
      }
    }
    setSessionId(id);
  }, []);

  // Hydrate history
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat/history`, {
          params: { session_id: sessionId },
        });
        if (cancelled) return;
        const msgs = (data?.messages || []).map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
        }));
        setMessages(msgs);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("history load failed", e);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Scroll on new message
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, sending]);

  const canSend = useMemo(
    () => input.trim().length > 0 && !sending && !!sessionId,
    [input, sending, sessionId]
  );

  const sendMessage = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || sending || !sessionId) return;

    // Direct intent: open audit wizard instead of asking the AI
    if (userWantsAudit(text)) {
      setInput("");
      setAuditOpen(true);
      return;
    }

    // Optimistic UI for the user message
    const userMsg = {
      id: `local-${Date.now()}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      const reply = data?.reply || "";
      const replyId = data?.message_id || `r-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: replyId, role: "assistant", content: reply },
      ]);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Couldn't reach Anthony right now. Try again in a moment.";
      toast.error(msg);
      // Roll back optimistic user message so they can retry cleanly
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      setInput(text);
    } finally {
      setSending(false);
      // Refocus composer
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleChip = (prompt) => {
    if (prompt === "__OPEN_AUDIT__") {
      setAuditOpen(true);
      return;
    }
    setInput(prompt);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReset = async () => {
    if (!sessionId || sending) return;
    try {
      await axios.post(`${API}/chat/reset`, { session_id: sessionId });
      setMessages([]);
      toast.success("Fresh start. Ask me anything.");
    } catch (_e) {
      toast.error("Couldn't reset right now.");
    }
  };

  const showEmpty = !hydrating && messages.length === 0 && !sending;

  return (
    <section
      data-testid="chat-panel"
      className="relative rounded-3xl bg-[rgba(14,11,13,0.72)] backdrop-blur-md border border-[rgba(244,236,224,0.18)] shadow-[0_18px_60px_rgba(0,0,0,0.4)] overflow-hidden"
    >
      <header className="flex items-center justify-between gap-3 px-5 pt-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[rgba(244,236,224,0.85)]" />
          <h2
            className="font-display font-bold text-[#F4ECE0] text-lg"
            data-testid="chat-panel-title"
          >
            Ask me anything
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="hidden lg:inline text-xs text-[rgba(244,236,224,0.65)] mr-1"
            data-testid="chat-panel-meta"
          >
            Powered by Claude Sonnet 4.5
          </span>
          <button
            type="button"
            onClick={() => {
              setContactReason(undefined);
              setContactOpen(true);
            }}
            data-testid="email-me-button"
            aria-label="Email Anthony directly"
            className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(244,236,224,0.22)] bg-[rgba(244,236,224,0.06)] px-3 py-1.5 text-xs text-[#F4ECE0] hover:bg-[rgba(244,236,224,0.12)] hover:border-[rgba(244,236,224,0.4)] transition-colors duration-200"
          >
            <Mail className="h-3.5 w-3.5" />
            Email me
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={!messages.length || sending}
            data-testid="chat-reset-button"
            aria-label="Reset conversation"
            className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(244,236,224,0.18)] px-3 py-1.5 text-xs text-[rgba(244,236,224,0.85)] hover:text-[#F4ECE0] hover:border-[rgba(244,236,224,0.36)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="scrollbar-thin px-5 py-4 h-[46vh] sm:h-[44vh] lg:h-[420px] overflow-y-auto"
        data-testid="chat-messages"
      >
        {showEmpty && (
          <div
            className="h-full flex items-center justify-center text-center"
            data-testid="chat-empty-state"
          >
            <p className="max-w-md text-sm text-[rgba(244,236,224,0.78)]">
              Curious about data, CRM, inclusive AI, neurodivergence or AI
              ethics? Ask away — I&apos;ll answer in my own voice, no waffle.
            </p>
          </div>
        )}

        {hydrating && (
          <div className="h-full flex items-center justify-center">
            <div className="typing-dots" aria-label="Loading">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        <ul className="space-y-3">
          <AnimatePresence initial={false}>
            {messages.map((m, idx) => (
              <motion.li
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                className={m.role === "user" ? "flex justify-end" : "flex"}
              >
                {m.role === "assistant" ? (
                  <div
                    data-testid={`chat-message-assistant-${idx}`}
                    className="max-w-[92%] sm:max-w-[82%] rounded-2xl bg-[rgba(244,236,224,0.08)] border border-[rgba(244,236,224,0.14)] px-4 py-3 text-[rgba(244,236,224,0.94)] leading-relaxed"
                  >
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[rgba(244,236,224,0.55)] mb-1">
                      Anthony
                    </p>
                    <p className="text-sm sm:text-[15px] whitespace-pre-wrap">
                      {renderRichText(m.content)}
                    </p>
                    {suggestsContactForm(m.content) && (
                      <button
                        type="button"
                        onClick={() => {
                          setContactReason(undefined);
                          setContactOpen(true);
                        }}
                        data-testid={`chat-inline-contact-cta-${idx}`}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#F4ECE0] text-[#0E0B0D] px-3 py-1.5 text-xs font-semibold hover:bg-[#F8F1E7] active:scale-[0.98] transition-[background-color,transform] duration-200"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Open the email form
                      </button>
                    )}
                    {suggestsAudit(m.content) && (
                      <button
                        type="button"
                        onClick={() => setAuditOpen(true)}
                        data-testid={`chat-inline-audit-cta-${idx}`}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FF4FA3] text-[#0E0B0D] px-3 py-1.5 text-xs font-semibold hover:brightness-110 active:scale-[0.98] transition-[filter,transform] duration-200"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Open the audit wizard
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    data-testid={`chat-message-user-${idx}`}
                    className="ml-auto max-w-[92%] sm:max-w-[82%] rounded-2xl bg-[rgba(244,236,224,0.16)] border border-[rgba(244,236,224,0.22)] px-4 py-3 text-[#F4ECE0] leading-relaxed whitespace-pre-wrap"
                  >
                    <p className="text-sm sm:text-[15px]">{m.content}</p>
                  </div>
                )}
              </motion.li>
            ))}
          </AnimatePresence>

          {sending && (
            <li className="flex">
              <div
                className="rounded-2xl bg-[rgba(244,236,224,0.08)] border border-[rgba(244,236,224,0.14)] px-4 py-3"
                data-testid="chat-loading-indicator"
              >
                <span className="typing-dots" aria-label="Anthony is typing">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </li>
          )}
        </ul>
      </div>

      <div className="px-5 pb-3 pt-1">
        <div
          className="flex flex-wrap gap-2"
          data-testid="chat-suggestion-chips"
        >
          {CHIPS.map((c) => (
            <button
              type="button"
              key={c.label}
              onClick={() => handleChip(c.prompt)}
              data-testid={`chat-suggestion-chip-${slug(c.label)}`}
              className={
                c.kind === "audit"
                  ? "rounded-full border border-[#FF4FA3]/60 bg-[rgba(255,79,163,0.14)] px-3 py-2 text-xs sm:text-sm text-[#F4ECE0] hover:bg-[rgba(255,79,163,0.22)] hover:border-[#FF4FA3] active:scale-[0.98] transition-[background-color,border-color,transform] duration-200 inline-flex items-center gap-1.5 font-semibold"
                  : "rounded-full border border-[rgba(244,236,224,0.18)] bg-[rgba(244,236,224,0.06)] px-3 py-2 text-xs sm:text-sm text-[#F4ECE0] hover:bg-[rgba(244,236,224,0.12)] hover:border-[rgba(244,236,224,0.32)] active:scale-[0.98] transition-[background-color,border-color,transform] duration-200"
              }
              disabled={sending}
            >
              {c.kind === "audit" && <ShieldCheck className="h-3.5 w-3.5" />}
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-end gap-2 rounded-2xl bg-[rgba(14,11,13,0.55)] border border-[rgba(244,236,224,0.18)] focus-within:border-[rgba(244,236,224,0.45)] transition-colors duration-200 p-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder="Ask Anthony a question…"
            aria-label="Ask Anthony a question"
            data-testid="chat-composer-input"
            className="min-h-[44px] max-h-40 resize-none bg-transparent border-0 text-[#F4ECE0] placeholder:text-[rgba(244,236,224,0.55)] focus-visible:ring-0 focus-visible:ring-offset-0 px-2"
            disabled={sending}
            maxLength={4000}
          />
          <Button
            type="button"
            onClick={() => sendMessage()}
            disabled={!canSend}
            aria-label="Send message"
            data-testid="chat-composer-send-button"
            className="rounded-full bg-[#F4ECE0] text-[#0E0B0D] hover:bg-[#F8F1E7] disabled:opacity-50 disabled:hover:bg-[#F4ECE0] active:scale-[0.98] transition-[background-color,transform] duration-200 h-10 px-4"
          >
            <Send className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline text-sm font-semibold">Send</span>
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-[rgba(244,236,224,0.55)] sm:hidden">
          Powered by Claude Sonnet 4.5
        </p>
      </div>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        defaultReason={contactReason}
      />
      <AuditDialog open={auditOpen} onOpenChange={setAuditOpen} />
    </section>
  );
}
